/**
 * Program spec registry (LEV-2442): what the shared accelerator-application
 * flow needs to know about each program, as data instead of an
 * `if YC / if Techstars` branch.
 *
 * Three tiers (T.A., Oct 17; see ADR 073, "Accelerator applications leave
 * Labs; one flow per program spec", decision 1):
 * - 'listed': a catalog row only, held in src/constants/programCatalog.ts.
 *   No flow, no questions, and no AppFormatId, so it never sits in
 *   PROGRAM_APPLICATIONS.
 * - 'landing': a landing page plus the shared flow, scored by the ONE
 *   generic lens and generic coaching categories below, never its own.
 * - 'premier': everything 'landing' gets, plus its own scoring lens,
 *   coaching categories, comparables and interview-prep style.
 *
 * The TIER, not a comment, enforces what a program needs: `LandingProgramSpec`
 * has no `scoringLens`/`coachingCategories`/`brutalPersona` fields at all, and
 * `PremierProgramSpec` requires them.
 *
 * Adding a 'landing' or 'premier' program (Plug and Play, Antler):
 * (.claude/skills/add-accelerator-program/SKILL.md walks every step below.)
 * 1. Its questions as data in src/constants/accelerators/questions/,
 *    shaped like techstars.ts there.
 * 2. Its AppFormatId and APP_FORMATS entry, if it has none yet
 *    (src/constants/accelerators/applications.ts). The router's format enum
 *    follows APP_FORMAT_IDS on its own.
 * 3. One ACCEL_APP_COACHING_COPY line built with programCoachingCopy (same
 *    file) from the spec's own programName and reviewerNoun; the registry
 *    test rejects YC's fallback copy and any other mismatch.
 * 4. Its launch flag in src/lib/analytics/featureFlagKeys.ts, created in
 *    PostHog dev in the same change.
 * 5. Its spec below, filed under its formatId, with `launchFlag` set. The
 *    generator, rubric and section map then come from the spec
 *    (questionDataFormatConfig in generators/questionData.ts), for every
 *    program alike; delete any hand-written generator the format had. Its
 *    "Your details" basics are completeness questions with a `detailKey`.
 *    A premier spec's coaching categories each name one scored question
 *    (`questionId`). A premier spec with
 *    `interviewPrep` gets the Interview prep stage (formatHasInterviewPrep).
 *    The panel's per-answer view, answer count, apply link and interview
 *    intro follow the spec too (src/lib/accelerators/applicationStage.ts).
 * 6. Its ACCELERATOR_ROW_PROGRAMS entry (src/constants/acceleratorsRow.ts)
 *    with its formatId; without it the lander's first stop has no row
 *    program to open.
 * 7. Usage labels for `accelerator-app.generate.<formatId>` and
 *    `accelerator-app.interview-prep.<formatId>` in the three maps of
 *    src/lib/ai/usage/labels.ts, and the generate operation in
 *    FOUNDER_ACTION_OPERATIONS (src/lib/teamStats/spend.ts). Unlabelled ones
 *    fall back to the `accelerator-app` prefix.
 * 8. Its lander: a PROGRAMS entry and PROGRAM_SLUGS (src/constants/programs.ts),
 *    its routes in src/middleware.ts, LANDING_PAGE_SLUGS
 *    (src/lib/analytics/events.ts), NESTED_QR_ROUTE_SLUGS
 *    (src/lib/landing/partnerQr.ts), a logo, and a route folder
 *    src/app/(landing)/<slug>/ holding: a thin page.tsx that exports
 *    `dynamic = "force-dynamic"` and calls ProgramLanderPage
 *    (src/app/(landing)/_program/) with `legacyLander: null` (never copy
 *    techstars/page.tsx: its legacyLander is Techstars' pre-straight-in
 *    page), plus qr/page.tsx and qr/card/route.tsx shaped like Techstars'.
 *    src/tests/landing.programRoutes.test.ts checks every list and file.
 *    The lander and the onboarding chat read the launch flag for the fixed
 *    id "anonymous", so a team-targeted launch flag keeps the lander 404 for
 *    everyone, staff included: preview the flow in Accelerator Apps, and
 *    open the lander by turning the flag on unconditionally.
 * 9. Its catalog entry's `appFormatId` (src/constants/programCatalog.ts), if
 *    it has an entry, so the catalog links to its application.
 *
 * Cross-compatible (no "use client"): read by generators, evaluators and the
 * deadline readers in src/lib/accelerators/, server and client alike.
 */
import type { ApplicationQuestion, ApplicationQuestionPart } from "@/constants/accelerators/questions/types";
import {
  TECHSTARS_APPLY_URL,
  TECHSTARS_PART_LABELS,
  TECHSTARS_QUESTIONS,
  TECHSTARS_SELECTION_ORDER,
} from "@/constants/accelerators/questions/techstars";
import { YC_QUESTIONS } from "@/constants/accelerators/questions/yc";
import { TECHSTARS_BRUTAL_PERSONA, YC_BRUTAL_PERSONA, type BrutalPersona } from "@/constants/brutalPersonas";
import {
  APP_FORMAT_IDS,
  getAccelAppCoachingCopy,
  type AccelAppCoachingCopy,
  type AppFormatId,
} from "@/constants/accelerators/applications";
import { PROGRAMS } from "@/constants/programs";
import { FEATURE_FLAGS, type FeatureFlagKey } from "@/lib/analytics/featureFlagKeys";

/** Score-affecting tools a program's detail view may turn on. */
export interface ProgramApplicationTools {
  oneLiners: boolean;
  visionSlider: boolean;
  /** Brutal mode (evaluateAppBrutal), voiced by getBrutalPersona. runBrutalMode refuses a format with this off. */
  brutal: boolean;
  /** A "what the greats said" quotes panel (src/constants/thinkBiggerQuotes.ts), filled only with quotes checked against their linked source. */
  quotes: boolean;
  pasteDraft: boolean;
}

/** A `weighs` line with the page it is read from and the date that page was checked. */
export interface SourcedPersonaLine {
  text: string;
  sourceUrl: string;
  verifiedOn: string;
}

/**
 * The reviewer a rubric reads the application as. `who` is a real person
 * only with public writing behind every `weighs` line; a line keeps quotation
 * marks only when its source page carries those exact words. A persona whose
 * lines are SourcedPersonaLine gets told, in the rubric, that each line is
 * Lev's reading and never a quote.
 */
export interface ProgramReviewerPersona {
  who: string;
  /** How the rubric introduces `who`, from an official page ("Techstars' founder and CEO"); omitted renders the name alone. */
  role?: string;
  /** What `who` weighs, one rubric bullet each. Sent to the model verbatim. */
  weighs: readonly (string | SourcedPersonaLine)[];
}

export function personaLineText(line: string | SourcedPersonaLine): string {
  return typeof line === "string" ? line : line.text;
}

export function isSourcedPersona(persona: ProgramReviewerPersona): boolean {
  return persona.weighs.length > 0 && persona.weighs.every((line) => typeof line !== "string");
}

/**
 * How a 'premier' program's answers are weighed. `selectionOrder` is omitted
 * (not null) for a program with no published ranked, score-capping list: YC
 * has no "answer X caps the overall score" rule the way Techstars does, and
 * inventing one would be a guess dressed up as data.
 */
export interface ProgramScoringLens {
  /** Priority order the program weighs answers by, most important first; a repeated entry (Techstars' "team") means that answer counts more than once. */
  selectionOrder?: readonly string[];
  /** A weak (below `weakThreshold`) answer to the first `selectionOrder` entry caps the overall score at `cappedAt`. */
  capRule: { cappedAt: number } | null;
  /** Below this score an answer is weak: the evaluator returns exactly one fix for it, and Improve never calls it strong. */
  weakThreshold: number;
  persona: ProgramReviewerPersona | null;
  /** One sentence the drafting prompt adds about what the answers must foreground. Sent to the model verbatim. */
  draftingEmphasis?: string;
  /**
   * The styles the five one-liners vary across, one each. Sent to the model
   * verbatim, so an example never carries a figure: the model copies it as
   * the company's own.
   */
  oneLinerStyles?: readonly string[];
  /** Program history the reviewer may cite when relevant. Facts only, sent to the model verbatim. */
  lore?: readonly string[];
}

/**
 * One coaching suggestion the rubric asks for on every scoring run. Pinned to
 * one scored question (`questionId`), so its suggestion lands on that
 * question's Improve row and never becomes an unscored row of its own.
 */
export interface ProgramCoachingCategory {
  name: string;
  questionId: string;
  /** What the suggestion asks the founder. Sent to the model verbatim. */
  ask: string;
}

/**
 * Keys of the server-side reference datasets a rubric may embed. A key, not
 * the data: this file is read by the client, and the dataset stays out of
 * its bundle (generators/questionData.ts maps the key to its prompt block).
 */
export type ProgramComparablesKey = "ycReference" | "techstarsReference";

interface ProgramApplicationBase {
  /** The formal name (PROGRAMS' fullName), for model prompts and formal text. */
  programName: string;
  /** What founder-facing copy calls the program (PROGRAMS' name), as the Application header does. */
  shortName: string;
  cohortLabel: string | null;
  /**
   * Null when the deadline is not yet known; never guessed. `iso` is the end
   * of the deadline day with an explicit UTC offset, and `label` is how
   * founder copy names it ("Nov 18"). One object, so a known date always has
   * a label to render.
   */
  deadline: { iso: string; label: string } | null;
  applyUrl: string | null;
  /** ISO date the facts on this spec were last checked against the program's official site. */
  verifiedOn: string;
  /**
   * The program's own brand colour (its brand guide, or its site's logo when
   * it publishes none) and the tint each Application header fades from to
   * white. `headerTint` is `primary` mixed with white in sRGB, as CSS
   * color-mix(in srgb, primary N%, white), and MUST keep at least 4.5:1
   * contrast for #1F2127 header text.
   */
  brand: { primary: string; headerTint: string };
}

interface ProgramApplicationFlowFields {
  formatId: AppFormatId;
  /**
   * The flag that launches this program for a viewer, or null for always
   * launched. The one launch gate: every "is it live" check reads this
   * through src/lib/accelerators/launchedFormats.ts.
   */
  launchFlag: FeatureFlagKey | null;
  /** Who reads the application, as founder copy names them (e.g. "YC partner", "Techstars reviewer"); the generic Brutal mode reviewer speaks as this noun. */
  reviewerNoun: string;
  partLabels: Record<ApplicationQuestionPart, string>;
  questions: readonly ApplicationQuestion[];
  /**
   * False keeps "## Company facts" (Lev memory, website, deck) out of a
   * question-data program's prompt and rubric. Unset means true.
   */
  usesCompanyFacts?: boolean;
  tools: ProgramApplicationTools;
  copy: AccelAppCoachingCopy;
}

/** The shared flow with GENERIC improvement: scored by the one lens and categories below, never its own. */
export interface LandingProgramSpec extends ProgramApplicationBase, ProgramApplicationFlowFields {
  tier: "landing";
}

/**
 * The shared flow with PROGRAM-SPECIFIC improvement. `metricsTracking` and
 * any other premier-only feature T.A. adds later (Oct 17: "might add other
 * features too for premier partners") are new fields on this interface only;
 * 'listed' and 'landing' never need to change shape to make room for them.
 */
export interface PremierProgramSpec extends ProgramApplicationBase, ProgramApplicationFlowFields {
  tier: "premier";
  scoringLens: ProgramScoringLens;
  coachingCategories: readonly ProgramCoachingCategory[];
  /** Brutal mode's toughest reviewer for this program. Only YC's may name real people (see brutalPersonas.ts). */
  brutalPersona: BrutalPersona;
  /** The reference dataset the rubric embeds for comparisons, or null when the program has none. */
  comparables: ProgramComparablesKey | null;
  interviewPrep: { style: string } | null;
}

/** A spec that runs the shared application flow, and so has an AppFormatId. */
export type ProgramApplicationFlowSpec = LandingProgramSpec | PremierProgramSpec;

/**
 * The ONE lens every 'landing'-tier program scores against until it earns
 * enough program-specific research to become 'premier'. Defined once, here;
 * a 'landing' spec has no field to hold a different one (see LandingProgramSpec).
 * The threshold matches Techstars' and is provisional: no program has
 * published a generic one, so T.A. signs it off before the first 'landing'
 * program ships.
 */
export const GENERIC_LANDING_SCORING_LENS: ProgramScoringLens = {
  capRule: null,
  weakThreshold: 7,
  persona: null,
  oneLinerStyles: [
    "What the company does and for whom, in plain words.",
    "The customer result: the outcome customers get, with a figure only when the application states one.",
    'The "[Known Company] for [New Domain]" formula.',
    "The problem made vivid: the pain the customer lives with today.",
    "The founders' edge: what the team knows or has done that makes this theirs to build.",
  ],
};

/**
 * Empty on purpose: a coaching category is pinned to one of its program's
 * questions, and a generic category has no question to pin to. A 'landing'
 * program is coached by its per-question weak-answer fixes alone.
 */
export const GENERIC_LANDING_COACHING_CATEGORIES: readonly ProgramCoachingCategory[] = [];

const YC_PART_LABELS: Record<ApplicationQuestionPart, string> = {
  "founder-profile": "Founders",
  company: "Application",
  attachment: "Required Materials",
  "program-selection": "Program Selection",
};

const TECHSTARS_LOOKING_FOR =
  "https://www.techstars.com/blog/advice/inside-a-techstars-accelerator-what-are-we-looking-for";
const COHEN_BUILT_IN_2014 = "https://builtin.com/articles/top-11-tips-la-startups-according-techstars-david-cohen";
const COHEN_WHY_1B = "https://davidgcohen.com/2026/07/29/why-some-startups-hit-1b-and-why-most-stall/";
const TECHSTARS_PERSONA_VERIFIED_ON = "2026-10-20";

/**
 * Each line is Lev's reading of David Cohen's public writing and Techstars'
 * published selection criteria, not a quote. His role is from
 * techstars.com/team/david-cohen ("Founder & CEO").
 */
const TECHSTARS_REVIEWER_PERSONA: ProgramReviewerPersona = {
  who: "David Cohen",
  role: "Techstars' founder and CEO",
  weighs: [
    {
      text: "The team above everything else: Techstars ranks team three times before market, traction and idea.",
      sourceUrl: TECHSTARS_LOOKING_FOR,
    },
    {
      text: "What the founders have already accomplished, how long they have worked together and how well they work together.",
      sourceUrl: TECHSTARS_LOOKING_FOR,
    },
    {
      text: "Persistence and the ability to learn, and whether these are the best people to make this particular company succeed.",
      sourceUrl: TECHSTARS_LOOKING_FOR,
    },
    {
      text: "Some form of validation that people want the product, even before revenue, paying customers or signups.",
      sourceUrl: TECHSTARS_LOOKING_FOR,
    },
    { text: "A sizable market that exists right now.", sourceUrl: TECHSTARS_LOOKING_FOR },
    { text: "The idea last, since ideas change over time with data and feedback.", sourceUrl: TECHSTARS_LOOKING_FOR },
    {
      text: "Where the founders' passion comes from, and founders who are never satisfied with the product.",
      sourceUrl: COHEN_BUILT_IN_2014,
    },
    { text: "Belief backed by data: conviction without evidence is one way startups die.", sourceUrl: COHEN_BUILT_IN_2014 },
    {
      text: "Focus: being very good at one specific thing in a big market before growing beyond it.",
      sourceUrl: COHEN_BUILT_IN_2014,
    },
    {
      text: "Doing the right things in the right order: a real pain point and a narrow focus before scaling.",
      sourceUrl: COHEN_WHY_1B,
    },
  ].map((line) => ({ ...line, verifiedOn: TECHSTARS_PERSONA_VERIFIED_ON })),
};

/** Typed as premier (not looked up through PROGRAM_APPLICATIONS) so callers read its lens and interview prep without a tier check. */
export const TECHSTARS_PROGRAM_APPLICATION: PremierProgramSpec = {
  tier: "premier",
  formatId: APP_FORMAT_IDS.TECHSTARS,
  launchFlag: FEATURE_FLAGS.ACCELERATOR_APPS_TECHSTARS,
  programName: PROGRAMS.techstars.fullName,
  shortName: PROGRAMS.techstars.name,
  // Sourced from the lander registry (PROGRAMS.techstars), not retyped, so
  // the Nov 18 date has exactly one place to change.
  cohortLabel: PROGRAMS.techstars.cohortLabel,
  deadline: { iso: PROGRAMS.techstars.deadlineIso, label: PROGRAMS.techstars.deadlineLabel },
  applyUrl: TECHSTARS_APPLY_URL,
  verifiedOn: "2026-10-16",
  // Phosphor, from brandguides.brandfolder.com/techstars-brand/color; tint at
  // 40% (raised from 20%, which read as pale mint rather than Techstars
  // green), 11.53:1 for #1F2127. Their guide says they use no gradients, so
  // the Techstars logo itself never sits on this tint (rowChrome
  // LOGO_BY_FORMAT).
  brand: { primary: "#39C463", headerTint: "#B0E7C1" },
  reviewerNoun: "Techstars reviewer",
  partLabels: TECHSTARS_PART_LABELS,
  questions: TECHSTARS_QUESTIONS,
  tools: { oneLiners: true, visionSlider: true, brutal: true, quotes: true, pasteDraft: true },
  copy: getAccelAppCoachingCopy(APP_FORMAT_IDS.TECHSTARS),
  scoringLens: {
    selectionOrder: TECHSTARS_SELECTION_ORDER,
    capRule: { cappedAt: 6 },
    weakThreshold: 7,
    // T.A., Oct 20: "Name David Cohen now", for scoring and Coach only;
    // TECHSTARS_BRUTAL_PERSONA keeps roles until Techstars OKs names, so
    // Brutal mode reads a rubric without this section (brutalRubric).
    persona: TECHSTARS_REVIEWER_PERSONA,
    draftingEmphasis:
      "Make the team the centerpiece: more than half of all the words should be about the team.",
    oneLinerStyles: [
      "What the company does and for whom, in plain words.",
      "The team: why these founders are the ones to build it.",
      "The customer result: the outcome customers get, with a figure only when the application states one.",
      'The "[Known Company] for [New Domain]" formula.',
      "The problem made vivid: the pain the customer lives with today.",
    ],
  },
  // D2 (2026-10-17) set three; T.A. added the comparable on Oct 20 ("Build
  // Techstars now"), on the same question YC's comparable coaches.
  coachingCategories: [
    {
      name: "Founder-Market Fit",
      questionId: "winning-team",
      ask: "Ask what in the founders' own experience makes them the team to solve this problem, rather than any capable team.",
    },
    {
      name: "Builder + Seller",
      questionId: "winning-team",
      ask: "Ask who on the team builds the product and who sells it, and what each has shipped or sold before.",
    },
    {
      name: "Validation",
      questionId: "traction",
      ask: "Ask for the most concrete proof that customers want this (paying customers, letters of intent, usage or interviews), counted exactly.",
    },
    {
      name: "Techstars Comparable",
      questionId: "competitors",
      ask: 'Pick the company from "Reference companies" below closest to this founder\'s space and ask: "[Company] went through [program] in [year] building [what it does]. What makes your team the one to build the equivalent in your market?"',
    },
  ],
  brutalPersona: TECHSTARS_BRUTAL_PERSONA,
  comparables: "techstarsReference",
  interviewPrep: {
    style:
      "A virtual screening committee (Managing Director, mentors, corporate partners, alumni), run as an " +
      "investment-memo conversation; founders answer in about 90 seconds each.",
  },
};

export const PROGRAM_APPLICATIONS: Partial<Record<AppFormatId, ProgramApplicationFlowSpec>> = {
  [APP_FORMAT_IDS.TECHSTARS]: TECHSTARS_PROGRAM_APPLICATION,
  [APP_FORMAT_IDS.YC]: {
    tier: "premier",
    formatId: APP_FORMAT_IDS.YC,
    launchFlag: null,
    programName: PROGRAMS.yc.fullName,
    shortName: PROGRAMS.yc.name,
    // Sourced from the lander registry (PROGRAMS.yc, with its source
    // comments), not retyped, so the W27 date has exactly one place to change.
    cohortLabel: PROGRAMS.yc.cohortLabel,
    deadline: { iso: PROGRAMS.yc.deadlineIso, label: PROGRAMS.yc.deadlineLabel },
    applyUrl: "https://www.ycombinator.com/apply",
    verifiedOn: "2026-10-19",
    // The logo's square fill on ycombinator.com and news.ycombinator.com (YC
    // publishes no brand guide); tint at 40% (raised from 20%, which read as
    // pale peach rather than YC orange), 10.29:1 for #1F2127.
    brand: { primary: "#FF6600", headerTint: "#FFC299" },
    reviewerNoun: "YC partner",
    partLabels: YC_PART_LABELS,
    questions: YC_QUESTIONS,
    usesCompanyFacts: true,
    tools: { oneLiners: true, visionSlider: true, brutal: true, quotes: true, pasteDraft: true },
    copy: getAccelAppCoachingCopy(APP_FORMAT_IDS.YC),
    scoringLens: {
      // No selectionOrder or capRule: YC publishes no ranked, score-capping list.
      capRule: null,
      weakThreshold: 7,
      draftingEmphasis:
        "Write in Paul Graham's voice: direct, short sentences, no jargon, no marketing-speak. When a concept is obscure, add a plain-English line so a generalist partner gets it at once.",
      // Each line is Lev's reading of Paul Graham's essays on paulgraham.com,
      // not a quote from them.
      persona: {
        who: "Paul Graham",
        weighs: [
          "Determination over intelligence.",
          "Short, honest sentences over marketing copy.",
          "Founders who discovered something by doing, not by reading about a market.",
          "Non-obvious insight: what does this founder know that others don't?",
          "Evidence of speed: what the team built, shipped or learned in the last two weeks.",
          "Founders who keep improving between the application and the interview.",
        ],
      },
      oneLinerStyles: [
        'The "[Known Company] for [New Domain]" formula.',
        "The specific traction number, using only a figure the application states.",
        "The market gap: the problem no one has solved yet.",
        'The paradox ("strangers sleeping in your house" for Airbnb).',
        'The founder\'s unfair advantage ("built by the team that scaled X to Y").',
      ],
      lore: [
        "Airbnb was turned down by the investors it first pitched, and Paul Graham almost didn't fund it.",
        "The Collison brothers were teenagers when they first went through YC, years before Stripe.",
        "The DoorDash founders delivered the first orders themselves.",
        "Dropbox proved demand with a short demo video before it launched.",
        "Reddit's founders came to YC with a mobile food-ordering idea and built Reddit instead.",
        "Paul Graham's essay Do Things That Don't Scale came from watching YC founders.",
      ],
    },
    // Each category lands on its question's Improve row (ProgramCoachingCategory).
    coachingCategories: [
      {
        name: "Founder-Idea Fit",
        questionId: "founders",
        ask: "Ask what in the founder's background makes them the person to solve this. Founders who lived the problem firsthand are the strongest signal Paul Graham describes.",
      },
      {
        name: "Unique Background Signal",
        questionId: "founders",
        ask: "Ask about the weird, surprising or counterintuitive thing about the founder that makes this click: the earnest, unfakeable detail Garry Tan reads for.",
      },
      {
        name: "Market Momentum",
        questionId: "why-this-idea",
        ask: "Ask for a specific stat or data point that shows this market is changing right now. To YC, timing matters as much as the idea.",
      },
      {
        name: "YC Comparable",
        questionId: "competitors",
        ask: 'Pick the most relevant company for this founder\'s space from "Reference companies" below and ask: "[Company] stood out because [reason]. What\'s your equivalent?"',
      },
      {
        name: "Bold Claim",
        questionId: "what-you-make",
        ask: 'Ask for the one sentence that would make a partner stop scrolling and say "wait, really?": the non-obvious insight, the fringe idea deeply technical people obsess over.',
      },
    ],
    brutalPersona: YC_BRUTAL_PERSONA,
    comparables: "ycReference",
    interviewPrep: {
      // Every claim below is Lev's prediction, each checked against YC's own
      // published guidance (fetched and quoted, not inferred from summaries):
      // - length/panel/medium: "YC interviews are 10-minute conversations
      //   over Zoom." / "Expect 2-3 YC Partners to be on the call."
      //   (ycombinator.com/interviews)
      // - decision timing: "We typically make decisions the same day as
      //   your interview." (ycombinator.com/apply)
      // verifiedOn: 2026-10-20
      style: "Expect a short, roughly 10-minute video call with 2-3 YC partners; YC typically decides the same day.",
    },
  },
};
